// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch, clearRecruiter, setRecruiter } from '../../store';
import { useMsal } from '../../recruiter/useMsal';
import RecruiterSidebar from '../../components/RecruiterSidebar';
import IdleSessionGuard from '../../components/IdleSessionGuard';
import { fetchInterviewHostDashboard } from '../../store/interviewHostSlice';
import styles from './authenticatedLayout.module.scss';

interface AuthenticatedLayoutProps {
  children: React.ReactNode;
  activeNav?: string;
  idleTimeoutMs?: number;
}

const AuthenticatedLayout: React.FC<AuthenticatedLayoutProps> = ({ children, activeNav = 'home', idleTimeoutMs }) => {
  const { instance } = useMsal();
  const dispatch = useAppDispatch();
  const user = useSelector((state: RootState) => state.auth.user);
  const { data } = useSelector((state: RootState) => state.interviewHost);

  // Fetch user data if not present - same pattern as home page
  useEffect(() => {
    if (!user && !data) {
      dispatch(fetchInterviewHostDashboard());
    } else if (data?.recruiter && !user) {
      // Set user in Redux when dashboard data is loaded
      dispatch(
        setRecruiter({
          name: data.recruiter.name,
          username: data.recruiter.email
        })
      );
    }
  }, [user, data, dispatch]);

  const handleLogout = async (): Promise<void> => {
    try {
      // Clear user from Redux and sessionStorage
      dispatch(clearRecruiter());

      // logoutPopup already performs the full-page redirect to /api/sso/logout;
      // navigating again here would cancel that request before the server can
      // destroy the session and clear the cookie.
      await instance.logoutPopup({
        postLogoutRedirectUri: '/login'
      });
    } catch (err) {
      console.error('Logout failed:', err);
      // Even if logout fails, clear user and redirect to login page for security
      dispatch(clearRecruiter());
      window.location.href = '/login';
    }
  };

  return (
    <div className={styles.layoutContainer}>
      <RecruiterSidebar activeNav={activeNav} user={user || undefined} onLogout={handleLogout} />
      <main className={styles.mainContent}>{children}</main>
      <IdleSessionGuard idleTimeoutMs={idleTimeoutMs} />
    </div>
  );
};

export default AuthenticatedLayout;
